import React, { useEffect, useState } from 'react';
import { Loading, Table, Button, Input, Notification, Tabs, Dialog, MessageBox, Tooltip } from 'element-react';
import { useHistory } from "react-router-dom";
import { API, Auth } from 'aws-amplify';
import NotesIcon from '../components/NotesIcon';
import DeleteIcon from '../components/DeleteIcon';
import { GET_SIGNED_URL, RFQ_API_URL } from '../utils/Constants'; 
import rfqStore from '../store/rfqStore';
import { IS_DEV } from '../utils/Constants'; 

function SellerRFQFeed(props) {
    // --- ESTADOS GENERALES ---
    const [loading, setLoading] = useState(false);
    const [rfqs, setRfqs] = useState([]); 
    const [originalRfqs, setOriginalRfqs] = useState([]); 
    const [filterTable, setFilterTable] = useState(''); 
    const [debouncedFilter, setDebouncedFilter] = useState(''); 
    const [activeTab, setActiveTab] = useState('activeRequests'); 
    const [myQuotations, setMyQuotations] = useState([]);
    const [originalMyQuotations, setOriginalMyQuotations] = useState([]);
    const history = useHistory();

    // --- ESTADOS PARA NOTAS DE PROPUESTAS (MIS COTIZACIONES) ---
    const [isProposalNotesModalVisible, setIsProposalNotesModalVisible] = useState(false);
    const [currentProposalNote, setCurrentProposalNote] = useState('');
    const [selectedProposalId, setSelectedProposalId] = useState(null);
    const [isProposalNoteEditing, setIsProposalNoteEditing] = useState(false);

    // --- ESTADOS PARA NOTAS DE RFQ (SOLICITUDES ACTIVAS) ---
    const [isRfqNotesModalVisible, setIsRfqNotesModalVisible] = useState(false);
    const [currentRfqNote, setCurrentRfqNote] = useState('');
    const [selectedRfq, setSelectedRfq] = useState(null);
    const [isRfqNoteEditing, setIsRfqNoteEditing] = useState(false);
    const [selectedRfqNoteId, setSelectedRfqNoteId] = useState(null);

    const loadMyQuotations = async () => {
        setLoading(true);
        // --- LÓGICA DE DESARROLLO VS. PRODUCCIÓN ---
        if (IS_DEV) {
            console.log("MODO DESARROLLO: Cargando propuestas locales desde el store.");
            
            const { mockProposals } = rfqStore.getState();
            
            const formattedQuotations = mockProposals.map(proposal => ({
                proposalDate: new Date(proposal.creation_date).toLocaleDateString('en-CA'),
                proposalId: proposal.id,
                productName: "Mock Product Name", 
                totalCost: `$${parseFloat(proposal.total_cost).toFixed(2)}`,
                validThrough: new Date(proposal.valid_date).toLocaleDateString('en-CA'),
                requiredBy: "N/A", 
                note: proposal.creator_notes || '',
                fileKey: proposal.file_key
            }));

            setMyQuotations(formattedQuotations);
            setOriginalMyQuotations(formattedQuotations);
            setLoading(false);
            return; // Detenemos la función 
        }

        // --- CÓDIGO ORIGINAL PARA PRODUCCIÓN ---
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            
            const response = await fetch(`${RFQ_API_URL}/getPropousalByOwner`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                }
            });

            if (!response.ok) {
                //throw new Error('Failed to fetch my quotations');
            }

            const data = await response.json();
            
            if (data.proposals) {
                // Filtra las cotizaciones para excluir las que tengan estatus 3 (retiradas)
                const activeProposals = data.proposals.filter(p => p.status_creator !== 3);
                
                const formattedQuotations = activeProposals.map(proposal => ({
                    proposalDate: new Date(proposal.creation_date).toLocaleDateString('en-CA'),
                    proposalId: proposal.id,
                    productName: proposal.product_name,
                    totalCost: `$${parseFloat(proposal.total_cost).toFixed(2)}`,
                    validThrough: new Date(proposal.valid_date).toLocaleDateString('en-CA'),
                    requiredBy: proposal.required_by_date ? new Date(proposal.required_by_date).toLocaleDateString('en-CA') : '-',
                    note: proposal.creator_notes,
                    fileKey: proposal.file_key
                }));
                setMyQuotations(formattedQuotations);
                setOriginalMyQuotations(formattedQuotations);
            }
        } catch (error) {
            console.error("Error fetching my quotations:", error);
            Notification.error('Could not load your quotations.');
        }finally {
            setLoading(false);
        }
    };

    const handleDeleteQuotation = async (quotationId) => {
        try {
            await MessageBox.confirm('Are you sure you want to delete this quotation?', 'Confirmation', { 
                type: 'warning' 
            });

            setLoading(true);
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await fetch(`${RFQ_API_URL}/updateProposalStatusById`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                },
                body: JSON.stringify({
                    proposalId: quotationId,
                    status: 4
                })
            });

            if (!response.ok) {
                throw new Error('API request to delete quotation failed');
            }

            setMyQuotations(currentQuotations => 
                currentQuotations.filter(q => q.proposalId !== quotationId)
            );

            Notification.success('Quotation successfully withdrawn!');
        } catch (error) {
            if (error !== 'cancel') {
                console.error("Error deleting quotation:", error);
                Notification.error('Failed to withdraw quotation.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleOpenProposalNotes = (quotation) => {
        setSelectedProposalId(quotation.proposalId);
        setCurrentProposalNote(quotation.note || '');
        setIsProposalNoteEditing(false);
        setIsProposalNotesModalVisible(true);
    };

    const handleSaveProposalNote = async () => {
        setLoading(true);
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            
            const response = await fetch(`${RFQ_API_URL}/updateProposalCreatorNotesById`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': token },
                body: JSON.stringify({
                    proposalId: selectedProposalId,
                    notes: currentProposalNote
                })
            });

            if (!response.ok) throw new Error('Failed to save proposal note');

            setIsProposalNotesModalVisible(false);
            Notification.success("Note saved successfully!");
            loadMyQuotations(); 

        } catch (error) {
            Notification.error("Failed to save note.");
            console.error("Error saving proposal note:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (fileKey) => {
        if (!fileKey) return;

        setLoading(true);
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await fetch(`${GET_SIGNED_URL}?fileName=${fileKey}`, {
                headers: { 'Authorization': token }
            });

            if (!response.ok) throw new Error('Failed to get download link.');

            const data = await response.json();
            window.open(data.url, '_blank');

        } catch (error) {
            console.error("Error getting signed URL:", error);
            Notification.error('Could not retrieve file for download.');
        } finally {
            setLoading(false);
        }
    };
    
    const handleOpenRfqNotes = async (rfq) => {
        setLoading(true);
        setSelectedRfq(rfq); // Guardamos el RFQ actual
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await fetch(`${RFQ_API_URL}/getRfqSellerNotesByRfqId/${rfq.id}`, {
                headers: { 'Authorization': token }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch RFQ notes');
            }

            const data = await response.json();
            
            // Revisamos si el arreglo 'notes' tiene algo
            if (data.notes && data.notes.length > 0) {
                // Si SÍ hay nota, guardamos el texto y el ID
                const existingNote = data.notes[0];
                setCurrentRfqNote(existingNote.notes);
                setSelectedRfqNoteId(existingNote.rfq_notes_id);
            } else {
                // Si NO hay nota, dejamos todo vacío/nulo
                setCurrentRfqNote('');
                setSelectedRfqNoteId(null);
            }

            setIsRfqNoteEditing(false);
            setIsRfqNotesModalVisible(true);

        } catch (error) {
            console.error("Error fetching RFQ note:", error);
            Notification.error("Could not load the note for this RFQ.");
        } finally {
            setLoading(false);
        }
    };
    
    const handleSaveRfqNote = async () => {        
        if (!selectedRfq) return;

        if (currentRfqNote.length > 4000) {
            Notification.error({
                title: 'Error',
                message: 'The note cannot exceed 4000 characters.'
            });
            return; 
        }
        
        setLoading(true);
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            
            const body = {
                rfqId: selectedRfq.id,
                notes: currentRfqNote,
                rfqNotesId: selectedRfqNoteId 
            };
            const response = await fetch(`${RFQ_API_URL}/createOrUpdateRfqSellerNotes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': token },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                throw new Error('Failed to save RFQ note');
            }

            Notification.success("Note saved successfully!");
            setIsRfqNotesModalVisible(false);
            
            loadRFQs(); 
            
        } catch (error) {
            console.error("Error saving RFQ note:", error);
            Notification.error("Failed to save the note.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRFQs();
        loadMyQuotations();
    }, []);

    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedFilter(filterTable);
        }, 300);

        return () => {
            clearTimeout(timerId);
        };
    }, [filterTable]);

    useEffect(() => {
        if (debouncedFilter) {
            // Filtra la lista de Active Requests
            const filteredRfqs = originalRfqs.filter(item =>
                item.productDescription.toLowerCase().includes(debouncedFilter.toLowerCase())
            );
            setRfqs(filteredRfqs);

            // Filtra la lista de My Quotations
            const filteredQuotations = originalMyQuotations.filter(item =>
                item.productName.toLowerCase().includes(debouncedFilter.toLowerCase())
            );
            setMyQuotations(filteredQuotations);

        } else {
            // Si el buscador está vacío, resetea ambas listas
            setRfqs(originalRfqs);
            setMyQuotations(originalMyQuotations);
        }
    }, [debouncedFilter, originalRfqs, originalMyQuotations]);

    const loadRFQs = async () => {
        setLoading(true);
        if (IS_DEV) {
            console.log("MODO DESARROLLO: Cargando RFQs locales desde el store.");
            
            const { mockRfqs } = rfqStore.getState();
            
            // Formateamos los datos locales para que la tabla los entienda
            const formattedData = mockRfqs.map(item => ({
                id: item.id,
                creationDate: new Date(item.created_date).toLocaleDateString('en-CA'),
                productDescription: item.productName,
                quantity: item.quantity,
                unit: item.unit,
                category: item.category[1],
                requiredByDate: item.requiredDate ? new Date(item.requiredDate).toLocaleDateString('en-CA') : '-',
                location: item.destination,
            }));
            
            setOriginalRfqs(formattedData);
            setRfqs(formattedData);
            setLoading(false);
            return; 
        }
        // --- CÓDIGO ORIGINAL PARA PRODUCCIÓN ---
        try {
            //const response = await API.get('NotificationServiceLambda', '/rfq/all', {});
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${RFQ_API_URL}/all`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'Authorization': token }
            });
            const result = await response.json();
            //filter only active status = 1
            console.log(result);
            const filteredRfqs = result.rfq.filter(item => item.status === 1);
            const formattedData = filteredRfqs.map(item => ({
                id: item.id,
                creationDate: new Date(item.created_date).toLocaleDateString('en-CA'),
                productDescription: item.product_name,
                quantity: item.quantity,
                unit: item.unit,
                category: item.category_name,
                requiredByDate: item.required_by_date ? new Date(item.required_by_date).toLocaleDateString('en-CA') : '-',
                location: item.destination,
            }));
            
            setOriginalRfqs(formattedData);
            setRfqs(formattedData);
        } catch (error) {
            console.error("Error cargando el feed de RFQs:", error);
            Notification.error('Failed to load RFQ Feed.');
        } finally {
            setLoading(false);
        }

    };

    const activeRequestsColumns = [
        { label: 'Date', prop: 'creationDate', width: 120, sortable: true },
        { label: 'Id', prop: 'id', width: 90,
            render: (row) => (
                <span>{`R${row.id}`}</span>
            )
        },
        { 
            label: 'Product', 
            prop: 'productDescription', 
            render: (row) => (
                <Tooltip content={row.productDescription} placement="bottom-start">
                    <span className="product-name truncate-text">{row.productDescription}</span>
                </Tooltip>
            ) 
        },
        { label: 'Quantity', prop: 'quantity', width: 100 },
        { label: 'Unit', prop: 'unit', width: 100 },
        { label: 'Category', prop: 'category', width: 180 },
        { label: 'Required By', prop: 'requiredByDate', width: 145, sortable: true },
        { label: 'Location', prop: 'location', width: 120, sortable: true },
        {
            label: 'Actions',
            width: 90,
            align: 'center',
            render: (row) => (
                <div onClick={(e) => e.stopPropagation()} className="action-icons">
                    <Tooltip content="Notes" placement="bottom">
                        <NotesIcon color="#FF6D73"
                            handleClick={() => handleOpenRfqNotes(row)} 
                            width={22} 
                            height={22} 
                        />
                    </Tooltip>
                </div>
            )

        }
    ];

    const myQuotationsColumns = [
        { label: 'Proposal Date', prop: 'proposalDate', width: 135 },
        { label: 'Id', prop: 'proposalId', width: 90 },
        { 
            label: 'Product', 
            prop: 'productName', 
            render: (row) => (
                <Tooltip content={row.productName} placement="bottom-start">
                    <span className="product-name truncate-text">{row.productName}</span>
                </Tooltip>
            ) 
        },
        { label: 'Total Cost', prop: 'totalCost', width: 110 },
        { 
            label: 'Proposal', 
            width: 120, 
            render: (row) => (
                row.fileKey 
                    ? <Button type="text" onClick={() => handleDownload(row.fileKey)}>Download</Button> 
                    : '–'
            )
        },
        { label: 'Valid Through', prop: 'validThrough', width: 140 },
        { label: 'Required By', prop: 'requiredBy', width: 130 },
        {
            label: 'Actions',
            width: 90,
            align: 'center',
            render: (row) => (
                <div onClick={(e) => e.stopPropagation()} className="action-icons">
                    <Tooltip content="Notes" placement="bottom">
                        <NotesIcon color="#FF6D73"
                            handleClick={() => handleOpenProposalNotes(row)} 
                            width={24} 
                            height={24} 
                        />
                    </Tooltip>
                    <Tooltip content="Delete" placement="bottom">
                        <DeleteIcon handleClick={() => handleDeleteQuotation(row.proposalId)} width={40}/>
                    </Tooltip>
                </div>
            )
        }
    ];

    return (
        <div className="RFQDirect">
            {loading && <Loading fullscreen={true} />}
            
            <div className="page-header">
                <div className="header-content">
                    <h1><span className="title-red">RFQ</span><span className="title-dark">Direct</span></h1>
                </div>
                <div className="actions-group">
                    <Input
                        icon="search"
                        placeholder="Search"
                        value={filterTable}
                        onChange={setFilterTable}
                    />
                </div>
            </div>

            <Tabs activeName={activeTab} onTabClick={(tab) => {
                    setActiveTab(tab.props.name);
                    if (tab.props.name === 'myQuotations') {
                        loadMyQuotations();
                    }
                }}>
                <Tabs.Pane label="Active Requests" name="activeRequests">
                    <p style={{ marginBottom: '15px', fontSize: '14px', color: '#383a3c' }}>
                        Requests for Quotation submitted by active Buyers in Bainub. <br/>
                        Click on any RFQ to check full details and submit a proposal if you are interested.
                    </p>
                    <div className="table-container responsive-table-container">
                        <Table
                            className="rfq-table clickable-rows"
                            data={rfqs}
                            columns={activeRequestsColumns}
                            fit={true}
                            emptyText="No active RFQs found."
                            onRowClick={(row) => {
                                history.push({ 
                                    pathname: `/rfq-details/${row.id}`, 
                                    state: { isSellerView: true }
                                });
                            }}
                        />
                    </div>
                </Tabs.Pane>
                
                <Tabs.Pane label="My Quotations" name="myQuotations">
                    <p style={{ marginBottom: '15px', fontSize: '14px', color: '#383a3c' }}>
                        Your submitted quotations.<br/>
                        Click on any item to check details and manage your proposal.
                    </p>
                    <div className="table-container responsive-table-container">
                        <Table
                            className="rfq-table"
                            data={myQuotations}
                            columns={myQuotationsColumns}
                            fit={true}
                            emptyText="You have not submitted any quotations yet."
                        />
                    </div>
                </Tabs.Pane>
            </Tabs>

            <Dialog
                title={`Notes for RFQ #${selectedRfq?.id || ''}`}
                visible={isRfqNotesModalVisible}
                onCancel={() => setIsRfqNotesModalVisible(false)}
                customClass="responsive-dialog"
            >
                <Dialog.Body>
                    {isRfqNoteEditing ? (
                        <>
                            <Input
                                type="textarea"
                                autosize={{ minRows: 5, maxRows: 8 }}
                                placeholder="Add a private note for this RFQ..."
                                value={currentRfqNote}
                                onChange={setCurrentRfqNote}
                            />
                            <div style={{ textAlign: 'right', fontSize: '12px', marginTop: '5px', color: currentRfqNote.length > 4000 ? 'red' : '#888' }}>
                                {currentRfqNote.length} / 4000
                            </div>
                        </>
                    ) : (
                        <p style={{ minHeight: '115px', whiteSpace: 'pre-wrap' }}>
                            {currentRfqNote || ''}
                        </p>
                    )}
                </Dialog.Body>
                <Dialog.Footer>
                    {isRfqNoteEditing ? (
                        <>
                            <Button type="secondary" className='btn-cancel-rfq' onClick={() => setIsRfqNoteEditing(false)}>Cancel</Button>
                            <Button type="primary" onClick={handleSaveRfqNote}>Save</Button>
                        </>
                    ) : (
                        <>
                            <Button type="secondary" onClick={() => setIsRfqNoteEditing(true)}>Edit</Button>
                            <Button type="primary" onClick={() => setIsRfqNotesModalVisible(false)}>OK</Button>
                        </>
                    )}
                </Dialog.Footer>
            </Dialog>

            <Dialog
                title={`Notes for Proposal #${selectedProposalId || ''}`}
                visible={isProposalNotesModalVisible}
                onCancel={() => setIsProposalNotesModalVisible(false)}
                customClass="responsive-dialog"
            >
                <Dialog.Body>
                    {isProposalNoteEditing ? (
                         <>
                            <Input
                                type="textarea"
                                autosize={{ minRows: 5, maxRows: 8 }}
                                placeholder="Add a note..."
                                value={currentProposalNote}
                                onChange={setCurrentProposalNote}
                            />
                             <div style={{ textAlign: 'right', fontSize: '12px', marginTop: '5px', color: currentProposalNote.length > 4000 ? 'red' : '#888' }}>
                                {currentProposalNote.length} / 4000
                            </div>
                        </>
                    ) : (
                        <p style={{ minHeight: '115px', whiteSpace: 'pre-wrap' }}>
                            {currentProposalNote || ''}
                        </p>
                    )}
                </Dialog.Body>
                <Dialog.Footer>
                    {isProposalNoteEditing ? (
                        <>
                            <Button type="secondary" onClick={() => setIsProposalNoteEditing(false)}>Cancel</Button>
                            <Button type="primary" onClick={handleSaveProposalNote}>Save</Button>
                        </>
                    ) : (
                         <>
                            <Button type="secondary" onClick={() => setIsProposalNoteEditing(true)}>Edit</Button>
                            <Button type="primary" onClick={() => setIsProposalNotesModalVisible(false)}>OK</Button>
                        </>
                    )}
                </Dialog.Footer>
            </Dialog>
        </div>
    );
}

export default SellerRFQFeed;